// KIADÁS al-nézet — a kiadás-oldal grafikonjai az Összegzésen belül.
//
//  1. A hónap kategória-megoszlása (fánk) és a BÉR / EGYÉB arány — egymás mellett.
//  2. A főkategóriák 12 hónapos halmozott trendje: mi nő, mi csökken.
//  3. A hónap legnagyobb szállítói: hova megy a pénz.
//
// CSAK FORINT (a lap egyenlegével azonos alap) — a devizás rész a kategória-panelen (`ExpenseCategoryPanel`)
// devizánként külön áll, és a lap figyelmeztető sávja kimondja.

import type { ExpenseAnalytics } from '@atlas/contracts';
import { addDecimals } from '@atlas/shared';
import { useTranslation } from 'react-i18next';

import { BarList, ChartCard, DonutChart } from '@/components/shared/charts';
import { categoryLabel } from '@/features/expenses/api';
import { StackedTrendChart, type TrendSeries } from '@/features/finance/components/finance-charts';
import { formatMoney, formatMoneyShort } from '@/lib/format-money';
import { formatMonthAxisLabel } from '@/lib/month';

/**
 * Hány kategória kap SAJÁT színt; a többi egy „Egyéb" szeletbe gyűlik. Hat-hét szín fölött a szeletek és a
 * halom-rétegek már nem különböztethetők meg egymástól.
 */
const NAMED_CATEGORIES = 5;
const PALETTE = [
  { fill: 'fill-brand', swatch: 'bg-brand' },
  { fill: 'fill-violet', swatch: 'bg-violet' },
  { fill: 'fill-info', swatch: 'bg-info' },
  { fill: 'fill-warning-400', swatch: 'bg-warning-400' },
  { fill: 'fill-success', swatch: 'bg-success' },
] as const;
const OTHER_COLOR = { fill: 'fill-neutral-400', swatch: 'bg-neutral-400' } as const;
const OTHER_KEY = '__other';

/**
 * A kategóriák SZÍN-SORRENDJE a 12 havi összegből (a BFF így rendezi): az első öt saját színt kap, a többi
 * „Egyéb". Ugyanaz a kiosztás a fánkon és a trenden — egy kategória mindkettőn ugyanazzal a színnel látszik.
 */
function categorySeries(
  analytics: ExpenseAnalytics,
  label: (c: ExpenseAnalytics['categoryTrend'][number]) => string,
  otherLabel: string,
): TrendSeries[] {
  const named = analytics.categoryTrend.slice(0, NAMED_CATEGORIES);
  const rest = analytics.categoryTrend.slice(NAMED_CATEGORIES);
  const series: TrendSeries[] = named.map((c, i) => ({
    key: c.categoryId ?? 'none',
    label: label(c),
    values: c.values,
    ...PALETTE[i]!,
  }));
  if (rest.length > 0) {
    series.push({
      key: OTHER_KEY,
      label: otherLabel,
      values: analytics.trend.map((_, m) =>
        rest.reduce((acc, c) => addDecimals(acc, c.values[m] ?? '0'), '0'),
      ),
      ...OTHER_COLOR,
    });
  }
  return series;
}

export function ExpenseAnalyticsPanels({
  analytics,
}: {
  analytics: ExpenseAnalytics | undefined;
}): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const fmt = (v: string): string => formatMoney(v, 'HUF', locale);
  const fmtShort = (v: string): string => formatMoneyShort(v, 'HUF', locale);

  if (!analytics) return null;

  const series = categorySeries(
    analytics,
    (c) =>
      c.categoryId === null
        ? t('finance.expenseAnalytics.uncategorized')
        : categoryLabel(c.categoryName, locale),
    t('finance.expenseAnalytics.other'),
  );
  const colorByKey = new Map(series.map((s) => [s.key, { fill: s.fill, swatch: s.swatch }]));
  const labelByKey = new Map(series.map((s) => [s.key, s.label]));
  // a cél-hónap szeletei: a trend UTOLSÓ oszlopa, a nulla szeletek nélkül
  const monthSlices = series
    .map((s) => ({ category: s.key, amount: s.values.at(-1) ?? '0' }))
    .filter((s) => Number(s.amount) !== 0);
  const { salary, other } = analytics.salarySplit;
  const salarySlices = [
    { category: 'salary', amount: salary },
    { category: 'other', amount: other },
  ].filter((s) => Number(s.amount) !== 0);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard
          title={t('finance.expenseAnalytics.byCategory')}
          emptyLabel={t('finance.summary.byCategoryEmpty')}
          isEmpty={monthSlices.length === 0}
        >
          <DonutChart
            data={monthSlices}
            fmt={fmt}
            fmtShort={fmtShort}
            categoryLabel={(k) => labelByKey.get(k) ?? k}
            colorOf={(k) => colorByKey.get(k) ?? OTHER_COLOR}
            ariaLabel={t('finance.expenseAnalytics.byCategory')}
          />
        </ChartCard>
        <ChartCard
          title={t('finance.expenseAnalytics.salarySplit')}
          emptyLabel={t('finance.summary.byCategoryEmpty')}
          isEmpty={salarySlices.length === 0}
        >
          <DonutChart
            data={salarySlices}
            fmt={fmt}
            fmtShort={fmtShort}
            categoryLabel={(k) =>
              t(`finance.expenseAnalytics.${k === 'salary' ? 'salary' : 'nonSalary'}`)
            }
            colorOf={(k) =>
              k === 'salary'
                ? { fill: 'fill-violet', swatch: 'bg-violet' }
                : { fill: 'fill-brand', swatch: 'bg-brand' }
            }
            ariaLabel={t('finance.expenseAnalytics.salarySplit')}
          />
        </ChartCard>
      </div>

      <ChartCard
        title={t('finance.expenseAnalytics.trend')}
        emptyLabel={t('finance.summary.byCategoryEmpty')}
        isEmpty={series.length === 0}
      >
        <StackedTrendChart
          months={analytics.trend.map((m) => m.month)}
          series={series}
          fmt={fmt}
          fmtShort={fmtShort}
          monthLabel={(m) => formatMonthAxisLabel(m, locale)}
          ariaLabel={t('finance.expenseAnalytics.trend')}
        />
      </ChartCard>

      <ChartCard
        title={t('finance.expenseAnalytics.topSuppliers')}
        emptyLabel={t('finance.summary.byCategoryEmpty')}
        isEmpty={analytics.topSuppliers.length === 0}
      >
        <BarList
          data={analytics.topSuppliers.map((s) => ({ name: s.supplierName, amount: s.netAmount }))}
          fmt={fmt}
          ariaLabel={t('finance.expenseAnalytics.topSuppliers')}
        />
      </ChartCard>
    </div>
  );
}
